import { type ActivityRequest } from "../types";

export type JoinActivityResult = {
  success: boolean;
  isFull: boolean;
  isRequestSent?: boolean;
};

export type NavigationTarget = {
  path: string;
  replace?: boolean;
  state?: Record<string, unknown>;
};

export type ToastTarget = {
  message: string;
  type: "success" | "error" | "info";
};

export function createMatchNavigationTarget(
  activityId: string,
  prefetchedActivity: ActivityRequest | null,
): NavigationTarget {
  return {
    path: `/match/${activityId}`,
    state: {
      prefetchedActivity,
    },
  };
}

export function createActivityFallbackTarget(
  activityId: string,
): NavigationTarget {
  return {
    path: `/activity/${activityId}`,
    replace: true,
  };
}

export function resolveActivityJoinFailure(
  activityId: string,
): { toast: ToastTarget; navigation: NavigationTarget } {
  return {
    toast: {
      message: "Failed to join. The activity might be full.",
      type: "error",
    },
    navigation: createActivityFallbackTarget(activityId),
  };
}

export function resolveActivityJoinError(
  activityId: string,
): { toast: ToastTarget; navigation: NavigationTarget } {
  return {
    toast: {
      message: "An error occurred while joining the activity.",
      type: "error",
    },
    navigation: createActivityFallbackTarget(activityId),
  };
}

export function resolveProfileJoinResult(
  activityId: string,
  result: JoinActivityResult,
):
  | { toast: ToastTarget; activeTab?: "joined"; navigation?: undefined }
  | { toast: ToastTarget; activeTab?: undefined; navigation: NavigationTarget } {
  if (result.success) {
    return {
      toast: {
        message: "Successfully joined the activity!",
        type: "success",
      },
      activeTab: "joined",
    };
  }

  if (result.isFull) {
    return {
      toast: {
        message: "Sorry, this activity is full.",
        type: "error",
      },
      navigation: createActivityFallbackTarget(activityId),
    };
  }

  return {
    toast: {
      message: "Failed to join activity. Please try again.",
      type: "error",
    },
    navigation: createActivityFallbackTarget(activityId),
  };
}

export function resolveProfileJoinError(
  activityId: string,
): { toast: ToastTarget; navigation: NavigationTarget } {
  return {
    toast: {
      message: "An error occurred while joining the activity.",
      type: "error",
    },
    navigation: createActivityFallbackTarget(activityId),
  };
}

export function selectProfileJoinPrefetchedActivity(
  activityId: string,
  candidateGroups: ActivityRequest[][],
): ActivityRequest | null {
  for (const group of candidateGroups) {
    const match = group.find((activity) => activity.id === activityId);
    if (match) {
      return match;
    }
  }

  return null;
}

export function resolveInviteResponseSuccess(
  activityId: string,
  accept: boolean,
): { toast: ToastTarget; navigation: NavigationTarget; delayMs: number } {
  return accept
    ? {
        toast: {
          message: "Invitation accepted! Redirecting to match...",
          type: "success",
        },
        navigation: {
          path: `/match/${activityId}`,
        },
        delayMs: 500,
      }
    : {
        toast: {
          message: "Invitation declined.",
          type: "info",
        },
        navigation: {
          path: "/",
        },
        delayMs: 500,
      };
}